/* The Roles tab of Team Simplified 1a: a fleet's Roles, edited once. Edits are
   a draft until Save, and saving a Role also updates everyone linked to it.
   New and Delete act at once: neither changes anybody's access. */

import { useTranslation } from 'react-i18next';
import type { TeamRole, TeamRoleInput } from '../../../api/client';
import { Btn, Icon } from '../../ChatAssistant/compact/kit';
import { C } from '../../ChatAssistant/compact/tokens';
import { font as f } from '../../ChatAssistant/compact/style';
import { PermissionEditor } from './RoleEditor';
import { ITEM_COUNT, accessOfRole, grantsOf, itemsOn } from './roleModel';

export function RolesPane({ roles, selected, onSelect, usedBy, fleetName, dirty, busy, onEdit, onCreateFrom, onDelete }: {
  /** The fleet's Roles with unsaved edits applied. */
  roles: TeamRole[];
  selected: TeamRole;
  onSelect: (id: string) => void;
  usedBy: (roleId: string) => number;
  fleetName: string;
  dirty: Set<string>;
  busy: boolean;
  onEdit: (roleId: string, input: TeamRoleInput) => void;
  onCreateFrom: (role: TeamRole) => void;
  onDelete: (role: TeamRole) => void;
}) {
  const { t } = useTranslation('agency');
  const input = (patch: Partial<TeamRoleInput>): TeamRoleInput =>
    ({ name: selected.name, description: selected.description, grants: selected.grants, ...patch });
  const access = accessOfRole(selected.grants);
  const n = usedBy(selected.id);

  return (
    <div className="ts-roles">
      <div className="tm-card" style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {roles.map((r) => (
          <button key={r.id} type="button" onClick={() => onSelect(r.id)} className="cpt-hover-row ts-role-row"
            style={{ background: r.id === selected.id ? 'var(--om-legacy-active)' : 'transparent' }}>
            <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <span className="tm-ellipsis" style={{ font: f(600, 13), color: C.text }}>
                {dirty.has(r.id) && <span className="tm-dirty-dot" title={t('fleet.save.unsavedTip')} />}
                {r.name}
              </span>
              <span style={{ font: f(400, 11.5), color: C.muted }}>{t('ts.usedBy', { count: usedBy(r.id) })}</span>
            </span>
            {r.locked && <Icon name="lock" size={11} color={C.muted} />}
            <span style={{ font: f(500, 11.5), color: C.purplePale }}>{itemsOn(accessOfRole(r.grants))}/{ITEM_COUNT}</span>
          </button>
        ))}
        <button type="button" disabled={busy} onClick={() => onCreateFrom(selected)} className="ts-add-role cpt-hover-border">
          <Icon name="plus" size={13} />{t('ts.newFrom', { name: selected.name })}
        </button>
      </div>

      <div className="tm-card" style={{ display: 'flex', flexDirection: 'column' }}>
        <div className="ts-role-head">
          <input value={selected.name} disabled={selected.locked} maxLength={40} aria-label={t('ts.roleName')}
            onChange={(e) => onEdit(selected.id, input({ name: e.target.value }))}
            className="tm-input" style={{ width: 200, height: 32, font: f(600, 14) }} />
          <input value={selected.description} disabled={selected.locked} maxLength={120} placeholder={t('ts.descPlaceholder')}
            onChange={(e) => onEdit(selected.id, input({ description: e.target.value }))}
            className="tm-input" style={{ flex: 1, height: 32 }} />
          {!selected.locked && (
            <Btn kind="muted" icon="trash-2" height={32} disabled={busy} title={t('ts.deleteTip')} onClick={() => onDelete(selected)}>{t('ts.delete')}</Btn>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: `1px solid ${C.divider}`, font: f(400, 12), color: C.sub, flexWrap: 'wrap' }}>
          <Icon name={selected.locked ? 'lock' : 'users'} size={13} color={C.purpleLt} />
          <span style={{ flex: 1, minWidth: 0 }}>
            {selected.locked ? t('ts.lockedNote') : t('ts.usedByLong', { count: n, fleet: fleetName })}
          </span>
          <span style={{ font: f(500, 12), color: C.purplePale }}>{t('ts.permCount', { k: itemsOn(access), n: ITEM_COUNT })}</span>
        </div>
        <div style={{ padding: 14 }}>
          <PermissionEditor access={access} disabled={selected.locked}
            onChange={(next) => onEdit(selected.id, input({ grants: grantsOf(next) }))} />
        </div>
      </div>
    </div>
  );
}
